<template>
  <BaseModal
    v-if="isOpen"
    is-open
    close-on-esc-key-click
    mobile-full-screen-modal
    @close="close"
  >
    <template #title>
      <div class="none sm:block md:text-center mb-8 md:text-2xl" data-test-id="reviews-modal-title">
        {{ props.venueName }} - Reviews and Ratings
      </div>
    </template>

    <ReviewModalMobileTopBar :data="data" :venue-name="venueName" @close="close" />

    <div class="flex h-full flex-col md:flex-row" data-test-id="reviews-modal-content">
      <ReviewModalSidebar class="reviews-modal-sidebar none sm:block pb-6 md:pb-0" :data="data" />

      <div class="h-full w-full">
        <div class="w-full h-full flex flex-col">
          <ReviewModalStats :data="data" class="sm:hidden mb-10" />

          <div class="flex im:items-center flex-col im:flex-row im:justify-end mb-2">
            <FormLabel class="font-normal text-init im:mb-0" input-id="order">
              Sort reviews by:
            </FormLabel>
            <BaseSelectField id="order" v-model="selectedSortingOption" class="im:ml-2" :options="sortingOptions" @update:modelValue="fetchReviews(true)" />
          </div>

          <BaseSkeleton v-if="isLoading" :lines="12" class="mt-10" />

          <div v-else class="flex flex-col mt-8 md:overflow-y-auto md:py-0.5 md:px-2">
            <div class="md:p-0.5 md:-m-0.5 w-full">
              <ReviewRow v-for="(review, index) in data.reviews" :key="index" :review="review" @track="emit('track', $event)" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </BaseModal>
</template>

<script setup>
import { defineEmits, defineExpose, defineProps, onMounted, ref, watch } from 'vue';

import BaseSkeleton from '@/components/BaseSkeleton.vue';
import BaseSelectField from '@/components/Ui/Forms/BaseSelectField.vue';
import FormLabel from '@/components/Ui/Forms/FormLabel.vue';
import BaseModal from '@/components/Ui/Modals/BaseModal.vue';
import { fillVariables } from '@/services/templates';
import request from '@/utils/request';

import ReviewModalMobileTopBar from './ReviewModalMobileTopBar.vue';
import ReviewModalSidebar from './ReviewModalSidebar.vue';
import ReviewModalStats from './ReviewModalStats.vue';
import ReviewRow from './ReviewRow.vue';

const props = defineProps({
  venueName: {
    type: String,
    default: '',
  },
  venueId: {
    type: Number,
    default: null,
  },
});

const emit = defineEmits(['track']);

let historyHelper = null;
const isOpen = ref(false);
const isLoading = ref(false);
const data = ref({});

const sortingOptions = ref([
  { value: 'most-relevant', label: 'Most relevant' },
  { value: 'newest', label: 'Newest' },
  { value: 'highest-rated', label: 'Highest rated' },
  { value: 'lowest-rated', label: 'Lowest rated' },
  { value: 'with-images', label: 'With images' },
]);
const selectedSortingOption = ref(sortingOptions.value[0].value);

const fetchReviews = (updated = false) => {
  isLoading.value = true;

  if (updated) {
    emit('track', {
      section: 'reviews_modal',
      element: 'sorting',
      element_value: selectedSortingOption.value,
      interaction: 'click',
    });
  }

  request(fillVariables('/modal/venue-review/{venue_id}/json?order={order}', {
    venue_id: props.venueId,
    order: selectedSortingOption.value,
  }))
    .then((response) => {
      data.value = response;
    })
    .catch((error) => {
      console.error('Error fetching reviews:', error);
    })
    .finally(() => {
      isLoading.value = false;
    });
};

watch(() => isOpen.value, (val) => {
  if (val) {
    fetchReviews(false);
  } else if (window.pswpLightboxReviews) {
    window.pswpLightboxReviews.destroy();
    window.pswpLightboxReviews = null;
  }
});

const initHistoryHelper = () => {
  historyHelper = App.components.history_state({
    onChange(popEvent) {
      if (popEvent?.state === 'reviews') {
        isOpen.value = true;
      } else {
        isOpen.value = false;
      }
      if (window.pswpLightboxReviews?.pswp?.isOpen) {
        window.pswpLightboxReviews.pswp.close();
      }
    },
  });
};

const close = () => {
  historyHelper.back();
};

const open = () => {
  isOpen.value = true;
  historyHelper.pushState('reviews', 'Reviews', '#reviews');
};

onMounted(() => {
  initHistoryHelper();

  window.triggerShowReviewsModal = () => {
    open();
  };

  if (window.location.hash === '#reviews') {
    window.history.replaceState(null, null, ' ');
    open();
  }
});

defineExpose({ open });
</script>

<style lang="scss" scoped>
@use "@sass/base/variables";

:deep(.ds-modal-wrapper--topAligned) {
  padding-top: 30px;
  padding-bottom: 30px;
}

:deep(.ds-modal) {
  @media #{variables.$for-medium-and-up} {
    max-width: 1200px;
    width: 100%;
  }

  @media #{variables.$for-mobile} {
    padding: 16px;
  }

  @media #{variables.$for-medium-and-up} {
    height: calc(100vh - 60px);
  }

  @media #{variables.$for-mobile} {
    .ds-modal-header {
      display: none;
    }
  }

  .ds-modal-content {
    height: calc(100% - 50px);

    @media #{variables.$for-mobile} {
      padding-top: 0;
      margin-top: 0
    }
  }

  .reviews-modal-sidebar {
    min-width: 370px;
    width: 100%;
    overflow: auto;
    margin-top: -2px;
    margin-bottom: 0;

    @media #{variables.$for-medium-and-up} {
      width: 370px;
      margin-right: 10px;
      padding-right: 36px;
    }
  }
}
</style>
